/**
 * CI-2 (CSE-FOLLOWUP-CONFLICT-DETECTION) — admin-only chip surfacing
 * internal CSE-claim conflicts (same `reportSectionId` + `field`,
 * different `value`).
 *
 * Reads off `report.qualityWarnings`'s `'cse-internal-conflict'`
 * variant stamped by `applyClaimsEmission`'s `onConflicts` callback.
 * Click opens a resolve dialog so operators can pick the canonical
 * value and persist trimmed `report.claims[]`.
 */
import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { SiteRagReport } from '../../../types/siteRag';
import {
  buildCseConflictTooltip,
  countCseConflictEntries,
  extractCseConflictEntries,
  hasCseConflicts,
} from '../../../lib/cseConflicts';
import { CseConflictResolveDialog } from './CseConflictResolveDialog';

export { hasCseConflicts, buildCseConflictTooltip, extractCseConflictEntries };

export interface CseConflictChipProps {
  report: SiteRagReport;
  isAdmin: boolean;
  onOpenSiteEvidence?: (opts?: { filterReportId?: string; focus?: 'conflicts' }) => void;
}

export const CseConflictChip: React.FC<CseConflictChipProps> = ({
  report,
  isAdmin,
  onOpenSiteEvidence,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!isAdmin) return null;
  if (!hasCseConflicts(report)) return null;

  const total = countCseConflictEntries(report);
  const tooltip = buildCseConflictTooltip(report);

  return (
    <>
      <button
        type="button"
        data-testid="cse-conflict-chip"
        title={tooltip}
        aria-live="polite"
        onClick={() => setDialogOpen(true)}
        className="my-2 inline-flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200 dark:hover:bg-amber-950/60 text-left"
      >
        <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden />
        <span>
          Structured claim conflicts ({total})
          <span className="block text-[10px] font-normal normal-case tracking-normal opacity-80">
            CSE – click to review &amp; pick values
          </span>
        </span>
      </button>
      <CseConflictResolveDialog
        report={report}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onOpenSiteEvidence={onOpenSiteEvidence}
      />
    </>
  );
};

export default CseConflictChip;
